/**
 * Bulk inventory CSV: parse + validate on the client so the merchant sees every
 * per-row error (and duplicates) before anything is submitted. Pure: no I/O.
 */
import { rupeesToPaise } from './money';
import { normalizeSerial, isValidSerial, maskSerial } from './serial';
import { GRADES } from './catalog';
import { conditionErrors, conditionKey, requiresOf } from '@/lib/conditions';
import { attributesFromForm, validateAttributes, isImei, customFieldsOf, parseYesNo } from '@/lib/productTypes';

// The unit columns every type shares; a type adds its spec columns (registry attributes, by key).
export const REQUIRED_COLUMNS = ['brand', 'model_name', 'internal_sku', 'serial', 'grade', 'condition'];
const UNIT_OPTIONAL = ['acquisition_cost', 'price', 'mrp', 'warranty_months', 'notes'];
export const MAX_IMPORT_ROWS = 500;

/**
 * Columns for one product type (B11-P2), from the registry: the shared unit columns,
 * the type's required specs, then series, its optional specs, battery health (types
 * with a battery) and the money and warranty columns, then the store's own fields
 * (MERCH-86, by key; the file may also head them with their label). No type, no
 * columns: the merchant picks one first (ADR 0009 §1).
 */
export function columnsFor(type) {
  if (!type) return { required: [], optional: [] };
  const required = type.attributes.filter((a) => a.required).map((a) => a.key);
  const optional = type.attributes.filter((a) => !a.required).map((a) => a.key);
  return {
    required: [...REQUIRED_COLUMNS, ...required],
    optional: ['series', ...optional, ...(type.battery ? ['battery_health_pct'] : []), ...UNIT_OPTIONAL, ...customFieldsOf(type).map((a) => a.key)],
  };
}

/** A header cell → the column it names: a custom field's label ("Fabric weight") reads as its key. */
function headerAliases(type) {
  return Object.fromEntries(customFieldsOf(type).map((a) => [headerKey(a.label), a.key]));
}

/** A row's cells with the store's own fields gathered under `custom`, as the spec form holds them. */
function gatherCustom(d, type) {
  const own = customFieldsOf(type);
  if (!own.length) return d;
  return { ...d, custom: Object.fromEntries(own.map((a) => [a.key, a.kind === 'boolean' ? boolCell(d[a.key]) : d[a.key] ?? ''])) };
}

// A yes/no cell (parseYesNo's words); anything else stays as written, and fails validation.
const boolCell = (v) => {
  const s = String(v ?? '').trim();
  const b = parseYesNo(s);
  return b !== null ? b : s === '' ? undefined : s;
};

const orList = (words) => (words.length > 1 ? `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}` : words[0] || '');

/**
 * The import help line for a type, from the served registry (useProductTypes().conditions):
 * "Grade A–D (only for refurbished or used); condition new, open_box, refurbished or used."
 */
export function conditionsHelp(type, conditions = []) {
  const allowed = (conditions || []).filter((c) => !type || !Array.isArray(type.conditions) || type.conditions.includes(c.key));
  if (!allowed.length) return '';
  const graded = allowed.filter((c) => requiresOf(c, type).grade).map((c) => c.key);
  const grade = graded.length ? `Grade ${GRADES[0]}–${GRADES[GRADES.length - 1]} (only for ${orList(graded)}); ` : '';
  return `${grade}condition ${orList(allowed.map((c) => c.key))}.`;
}

const csvCell = (v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));

/** A template CSV for a type: the header, then its registry `example` rows, else one generated row. */
export function templateFor(type) {
  if (!type) return '';
  const { required, optional } = columnsFor(type);
  const cols = [...required, ...optional];
  const example = (a) => (a.kind === 'select' ? a.options[0] : a.kind === 'boolean' ? 'yes' : a.kind === 'number' ? String(a.min != null && a.min > 0 ? a.min * 2 : 1) : (a.suggestions && a.suggestions[0]) || 'Example');
  const generated = {
    brand: 'Brand', model_name: 'Model', internal_sku: `${type.key.toUpperCase().slice(0, 3)}-001`,
    // The type's first condition (registry order); a grade only where a condition needs one, so none here.
    serial: type.serial === 'imei' ? '490154203237518' : 'SN12345678', grade: '', condition: (type.conditions || [])[0] || '',
    ...(type.battery ? { battery_health_pct: '90' } : {}), price: '9999', warranty_months: '3',
    ...Object.fromEntries(type.attributes.map((a) => [a.key, example(a)])),
  };
  // The store's own fields get an example value in every sample row (the registry's rows know none).
  const own = Object.fromEntries(customFieldsOf(type).map((a) => [a.key, example(a)]));
  const samples = (Array.isArray(type.example) && type.example.length ? type.example : [generated]).map((row) => ({ ...row, ...own }));
  return [cols.join(','), ...samples.map((row) => cols.map((c) => csvCell(row[c] ?? '')).join(','))].join('\n');
}

/**
 * RFC-4180-ish CSV parser: quoted fields, "" escapes, commas/newlines inside
 * quotes, CRLF, and a leading BOM. Returns an array of string arrays.
 */
export function parseCsv(text) {
  const src = String(text || '').replace(/^﻿/, '');
  const out = [];
  let row = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field);
      out.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    out.push(row);
  }
  return out.filter((r) => r.some((v) => v.trim() !== ''));
}

const headerKey = (h) => String(h || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

function intIn(value, min, max) {
  if (value === '') return true;
  if (!/^\d+$/.test(value)) return false;
  const n = Number(value);
  return n >= min && n <= max;
}

/**
 * Validate parsed CSV rows.
 *   existingSkus  SKUs that already exist in the store (from a crud lookup)
 * Returns { headerErrors, rows: [{ line, data, errors, payload }], validCount, errorCount }.
 * `payload` is the normalised row (paise, ints, upper-case serial) for valid rows only.
 */
export function validateImport(table, { existingSkus = [], type = null, conditions = [] } = {}) {
  const headerErrors = [];
  if (!type) return { headerErrors: ['Pick what this file holds (a product type) first.'], rows: [], validCount: 0, errorCount: 0 };
  if (!table || table.length === 0) return { headerErrors: ['The file is empty.'], rows: [], validCount: 0, errorCount: 0 };
  const cols = columnsFor(type);
  const allColumns = [...cols.required, ...cols.optional];
  const aliases = headerAliases(type);
  const headers = table[0].map(headerKey).map((h) => aliases[h] || h);
  const missing = cols.required.filter((c) => !headers.includes(c));
  if (missing.length) headerErrors.push(`Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`);
  const unknown = headers.filter((h) => h && !allColumns.includes(h));
  if (unknown.length) headerErrors.push(`Unknown column${unknown.length > 1 ? 's' : ''} ignored: ${unknown.join(', ')}.`);
  const body = table.slice(1);
  if (body.length === 0) headerErrors.push('The file has a header but no rows.');
  if (body.length > MAX_IMPORT_ROWS) headerErrors.push(`At most ${MAX_IMPORT_ROWS} rows per import (found ${body.length}).`);
  if (missing.length) return { headerErrors, rows: [], validCount: 0, errorCount: 0 };

  const existing = new Set(existingSkus.map((s) => String(s).trim().toUpperCase()));
  const skuLines = new Map();
  const serialLines = new Map();
  const rows = body.slice(0, MAX_IMPORT_ROWS).map((cells, i) => {
    const data = {};
    headers.forEach((h, idx) => {
      if (allColumns.includes(h)) data[h] = String(cells[idx] == null ? '' : cells[idx]).trim();
    });
    return { line: i + 2, data, errors: [] };
  });

  for (const r of rows) {
    const d = r.data;
    const e = r.errors;
    // The grade cell may be empty where the condition has no grade (D98): conditionErrors decides.
    // Required specs are reported by validateAttributes below, with their labels.
    for (const c of REQUIRED_COLUMNS) if (c !== 'grade' && c !== 'condition' && !d[c]) e.push(`${c} is required`);
    const ce = conditionErrors({ condition: d.condition, grade: d.grade }, { conditions, type, grades: GRADES });
    for (const k of ['condition', 'grade']) if (ce[k]) e.push(ce[k]);
    if (type.serial === 'imei') {
      if (d.serial && !isImei(d.serial)) e.push('serial must be a valid 15-digit IMEI');
    } else if (d.serial && !isValidSerial(d.serial)) e.push('serial must be 5–30 letters or digits');
    for (const msg of Object.values(validateAttributes(type, gatherCustom(d, type)))) e.push(msg);
    if (d.battery_health_pct && !intIn(d.battery_health_pct, 0, 100)) e.push('battery_health_pct must be 0–100');
    if (d.warranty_months && !intIn(d.warranty_months, 0, 60)) e.push('warranty_months must be 0–60');
    for (const c of ['acquisition_cost', 'price', 'mrp']) {
      if (d[c] && rupeesToPaise(d[c]) == null) e.push(`${c} must be an amount in rupees`);
    }
    const price = rupeesToPaise(d.price);
    const mrp = rupeesToPaise(d.mrp);
    if (price != null && mrp != null && mrp < price) e.push('mrp cannot be below price');

    if (d.internal_sku) {
      const sku = d.internal_sku.toUpperCase();
      if (existing.has(sku)) e.push(`internal_sku ${d.internal_sku} already exists in your inventory`);
      if (skuLines.has(sku)) e.push(`duplicate internal_sku (also on line ${skuLines.get(sku)})`);
      else skuLines.set(sku, r.line);
    }
    if (d.serial) {
      const serial = normalizeSerial(d.serial);
      if (serialLines.has(serial)) e.push(`duplicate serial (also on line ${serialLines.get(serial)})`);
      else serialLines.set(serial, r.line);
    }
    if (e.length === 0) r.payload = toPayload(d, type);
  }

  const errorCount = rows.filter((r) => r.errors.length).length;
  return { headerErrors, rows, validCount: rows.length - errorCount, errorCount };
}

/** One valid row for the server: the shared unit fields, `product_type` and its `attributes`. */
function toPayload(d, type) {
  const int = (v) => (v === '' || v == null ? null : Number(v));
  return {
    product_type: type.key,
    attributes: attributesFromForm(type, gatherCustom(d, type)),
    brand: d.brand,
    series: d.series || null,
    model_name: d.model_name,
    internal_sku: d.internal_sku.toUpperCase(),
    serial: normalizeSerial(d.serial),
    serial_masked: maskSerial(d.serial),
    grade: d.grade ? d.grade.toUpperCase() : null,
    condition: conditionKey(d.condition),
    battery_health_pct: type.battery ? int(d.battery_health_pct) : null,
    acquisition_cost_paise: rupeesToPaise(d.acquisition_cost) || 0,
    price_paise: rupeesToPaise(d.price),
    mrp_paise: rupeesToPaise(d.mrp),
    warranty_months: int(d.warranty_months) || 0,
    notes: d.notes || null,
  };
}
